import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { 
  Users,
  Building2,
  MapPin,
  Briefcase,
  Search,
  RefreshCw,
  Mail,
  Phone,
  UserX,
  CheckCircle,
  Trash2,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminMembers() {
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({ total: 0, with_organization: 0, independent: 0, organizations: 0, agents: 0, regional_offices: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  // Selection states for bulk delete
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members || []);
      setStats({
        total: response.data.total || 0,
        with_organization: response.data.with_organization || 0,
        independent: response.data.independent || 0,
        organizations: response.data.organizations || 0,
        agents: response.data.agents || 0,
        regional_offices: response.data.regional_offices || 0
      });
      setSelectedIds([]); // Clear selection on refresh
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchMembers();
    }
  }, [token]);

  // Filter members based on tab and search
  const filteredMembers = members.filter(member => {
    const matchesSearch = !searchTerm || 
      member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.organization_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (activeTab === 'all') return true;
    if (activeTab === 'organizations') return member.role === 'organization';
    if (activeTab === 'with_org') return !!member.organization_id && member.role !== 'organization';
    if (activeTab === 'independent') return !member.organization_id && member.role !== 'organization';
    if (activeTab === 'regional') return member.role === 'regional_office';
    if (activeTab === 'agents') return member.role === 'agent';
    return true;
  });

  // Handle select all
  const handleSelectAll = () => {
    if (selectedIds.length === filteredMembers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredMembers.map(m => m.id));
    }
  };

  // Handle single selection
  const handleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    setDeleting(true);
    try {
      await axios.post(`${API}/api/admin/users/bulk-delete`, 
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`${selectedIds.length} member(s) deleted`);
      setSelectedIds([]);
      setDeleteDialogOpen(false);
      fetchMembers();
    } catch (error) {
      console.error('Error deleting members:', error);
      toast.error('Failed to delete members');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="super_admin">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl">Registered Members</h2>
            <p className="text-muted-foreground">View all organizations, agents, and regional offices in the system</p>
          </div>
          <div className="flex gap-2">
            {selectedIds.length > 0 && (
              <Button 
                variant="destructive" 
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="bulk-delete-members"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedIds.length})
              </Button>
            )}
            <Button variant="outline" onClick={fetchMembers}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Members</p>
                  <p className="text-2xl font-heading font-bold text-blue-500">{stats.total}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Organizations</p>
                  <p className="text-2xl font-heading font-bold text-green-500">{stats.organizations}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Agents</p>
                  <p className="text-2xl font-heading font-bold text-amber-500">{stats.agents}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Regional Offices</p>
                  <p className="text-2xl font-heading font-bold text-purple-500">{stats.regional_offices}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Independent</p>
                  <p className="text-2xl font-heading font-bold text-slate-500">{stats.independent}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-slate-500/10 flex items-center justify-center">
                  <UserX className="w-5 h-5 text-slate-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Members Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <div>
                <CardTitle className="font-heading">All Members</CardTitle>
                <CardDescription>Organizations, Agents, and Regional Offices registered in the system</CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, org..."
                    className="pl-9 w-full sm:w-64"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList>
                <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
                <TabsTrigger value="organizations">Organizations ({stats.organizations})</TabsTrigger>
                <TabsTrigger value="agents">Agents ({stats.agents})</TabsTrigger>
                <TabsTrigger value="regional">Regional ({stats.regional_offices})</TabsTrigger>
                <TabsTrigger value="independent">Independent ({stats.independent})</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {filteredMembers.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedIds.length === filteredMembers.length && filteredMembers.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-members"
                        />
                      </TableHead>
                      <TableHead>Member</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Parent Organization</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Credits</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMembers.map((member) => (
                      <TableRow key={member.id} className={selectedIds.includes(member.id) ? 'bg-muted/50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(member.id)}
                            onCheckedChange={() => handleSelect(member.id)}
                            data-testid={`select-member-${member.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{member.name}</p>
                            {member.representative_name && (
                              <p className="text-xs text-muted-foreground">{member.representative_name}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="w-3 h-3 text-muted-foreground" />
                              {member.email}
                            </div>
                            {member.phone && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Phone className="w-3 h-3" />
                                {member.phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {member.role === 'organization' ? (
                            <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                              <Building2 className="w-3 h-3 mr-1" />
                              Organization
                            </Badge>
                          ) : member.role === 'regional_office' ? (
                            <Badge className="bg-purple-500/10 text-purple-500 border-purple-500/20">
                              <MapPin className="w-3 h-3 mr-1" />
                              Regional Office
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              <Briefcase className="w-3 h-3 mr-1" />
                              Agent
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {member.role === 'organization' ? (
                            <span className="text-muted-foreground text-sm italic">-</span>
                          ) : member.organization_name ? (
                            <div>
                              <div className="flex items-center gap-1">
                                <Building2 className="w-3 h-3 text-green-500" />
                                <span className="font-medium text-sm">{member.organization_name}</span>
                              </div>
                              <p className="text-xs text-muted-foreground">{member.organization_email}</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm italic">
                              No organization
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                            {member.status === 'active' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {member.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{member.credits}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No members found</p>
                <p className="text-sm mt-2">
                  {searchTerm ? 'Try a different search term' : 'No agents or regional offices registered yet'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Members</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete {selectedIds.length} member(s)? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleBulkDelete}
                disabled={deleting}
                data-testid="confirm-bulk-delete-members"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {selectedIds.length} Member(s)
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
